import React, { useEffect, useId, useMemo, useRef, useState } from 'react';

function orderSelectedValues(selectedValues, options) {
  const selectedSet = new Set(Array.isArray(selectedValues) ? selectedValues : []);
  return options.map((option) => option.value).filter((value) => selectedSet.has(value));
}

function buildSummaryText({ options, selectedValues, allLabel, noneLabel }) {
  if (!options.length) return noneLabel;
  if (!selectedValues.length) return noneLabel;
  if (selectedValues.length === options.length) return allLabel;

  if (selectedValues.length === 1) {
    return options.find((option) => option.value === selectedValues[0])?.label || selectedValues[0];
  }

  return `${selectedValues.length} selecionados`;
}

export default function FilterMultiSelect({
  label,
  options,
  selectedValues,
  onChange,
  allLabel,
  noneLabel
}) {
  const instanceId = useId();
  const containerRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const orderedSelectedValues = useMemo(
    () => orderSelectedValues(selectedValues, options),
    [options, selectedValues]
  );

  const summaryText = useMemo(
    () => buildSummaryText({ options, selectedValues: orderedSelectedValues, allLabel, noneLabel }),
    [allLabel, noneLabel, options, orderedSelectedValues]
  );

  useEffect(() => {
    const handleCloseRequest = (event) => {
      if (event.detail !== instanceId) setIsOpen(false);
    };

    window.addEventListener('task-hub:close-filter-menus', handleCloseRequest);
    return () => window.removeEventListener('task-hub:close-filter-menus', handleCloseRequest);
  }, [instanceId]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const handleToggleOpen = (event) => {
    event.preventDefault();
    const nextOpen = !isOpen;
    setIsOpen(nextOpen);
    if (nextOpen) {
      window.dispatchEvent(new CustomEvent('task-hub:close-filter-menus', { detail: instanceId }));
    }
  };

  const handleToggle = (value) => {
    const selectedSet = new Set(orderedSelectedValues);
    if (selectedSet.has(value)) selectedSet.delete(value);
    else selectedSet.add(value);
    onChange(orderSelectedValues([...selectedSet], options));
  };

  return (
    <details className="multi-filter" open={isOpen} ref={containerRef}>
      <summary className="multi-filter__summary" onClick={handleToggleOpen}>
        <span className="multi-filter__heading">
          <span className="multi-filter__label">{label}</span>
          <strong className="multi-filter__value">{summaryText}</strong>
        </span>
        <span className="multi-filter__caret" aria-hidden="true">▾</span>
      </summary>

      <div className="multi-filter__menu">
        <div className="multi-filter__actions">
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => onChange(options.map((option) => option.value))}>
            Todos
          </button>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => onChange([])}>
            Limpar
          </button>
        </div>

        <div className="multi-filter__options">
          {options.map((option) => (
            <label key={option.value} className="multi-filter__option">
              <input
                type="checkbox"
                checked={orderedSelectedValues.includes(option.value)}
                onChange={() => handleToggle(option.value)}
              />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
      </div>
    </details>
  );
}
